import type { ScorecardTargetProvenance } from './types';

interface ScorecardTargetProvenancePanelProps {
    provenance: ScorecardTargetProvenance | null | undefined;
}

const statusClassName = (status: ScorecardTargetProvenance['validation']['status']): string => {
    if (status === 'valid') return 'border-green-600 bg-green-500/10 text-green-300';
    if (status === 'invalid') return 'border-red-600 bg-red-500/10 text-red-300';
    if (status === 'warning') return 'border-amber-600 bg-amber-500/10 text-amber-200';
    return 'border-zinc-700 bg-zinc-800 text-zinc-300';
};

const formatNullableId = (value: number | null | undefined): string => (value == null ? '—' : `#${value}`);

const ScorecardTargetProvenancePanel = ({ provenance }: ScorecardTargetProvenancePanelProps) => {
    if (!provenance) return null;

    const optionStrategyLabel = provenance.optionTarget?.optionStrategies.length
        ? provenance.optionTarget.optionStrategies
              .map((strategy) => `#${strategy.id} ${strategy.name} (${strategy.openTradeType})`)
              .join(', ')
        : '—';

    const optionSelectionLabel = provenance.optionSelections.length
        ? provenance.optionSelections
              .map((group) => {
                  const selections = group.selections.length
                      ? group.selections
                            .map((selection) => `${selection.optionBuyingStrategyId}:${selection.openTradeType} (${selection.count})`)
                            .join(', ')
                      : 'none';

                  return `${group.role} #${group.experimentId}: ${selections}`;
              })
              .join(' | ')
        : '—';

    return (
        <div className="rounded border border-zinc-700 bg-zinc-900/40 p-3 text-xs text-zinc-300">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium text-white">Target Provenance</div>
                <span className={`rounded border px-2 py-0.5 text-[11px] font-medium ${statusClassName(provenance.validation.status)}`}>
                    {provenance.validation.status}
                </span>
            </div>

            <div className="mt-3 grid gap-2 md:grid-cols-2">
                <div>
                    Target metric: <span className="font-medium text-white">{provenance.targetMetric}</span>
                </div>
                <div>
                    Return definition:{' '}
                    <span className="font-medium text-white">
                        {provenance.futuresReturnDefinition?.label ?? provenance.optionReturnDefinition?.label ?? '—'}
                    </span>
                </div>
                {provenance.optionReturnDefinition && (
                    <>
                        <div>
                            BTO denominator:{' '}
                            <span className="font-medium text-white">{provenance.optionReturnDefinition.buyToOpenDenominator}</span>
                        </div>
                        <div>
                            STO denominator:{' '}
                            <span className="font-medium text-white">{provenance.optionReturnDefinition.sellToOpenDenominator}</span>
                        </div>
                    </>
                )}
                <div>
                    Experiment: <span className="font-medium text-white">{formatNullableId(provenance.experimentId)}</span>
                </div>
                <div>
                    Baseline experiment: <span className="font-medium text-white">{formatNullableId(provenance.baselineExperimentId)}</span>
                </div>
                <div>
                    Discovery run: <span className="font-medium text-white">{formatNullableId(provenance.generation?.discoveryRunId)}</span>
                </div>
                <div>
                    Baseline set: <span className="font-medium text-white">{formatNullableId(provenance.baselineSetId)}</span>
                </div>
                <div>
                    Training strategy: <span className="font-medium text-white">{formatNullableId(provenance.trainingStrategyId)}</span>
                </div>
                <div>
                    Training entry version:{' '}
                    <span className="font-medium text-white">{formatNullableId(provenance.trainingTradeLogicV2EntryVersionId)}</span>
                </div>
            </div>

            {provenance.futuresReturnDefinition && (
                <div className="mt-3 space-y-2 text-zinc-300">
                    <p>{provenance.futuresReturnDefinition.description}</p>
                    <p>{provenance.futuresReturnDefinition.denominator}</p>
                    <p>{provenance.futuresReturnDefinition.labelPrecision}</p>
                    <p>{provenance.futuresReturnDefinition.scope}</p>
                    {provenance.futuresTarget && (
                        <p>
                            Native outcome set #{provenance.futuresTarget.realizedOutcomeSetId}; ETF asset #
                            {provenance.futuresTarget.signalAssetId}
                            {' → '}futures product #{provenance.futuresTarget.productId}; configuration #
                            {provenance.futuresTarget.configurationId}.
                        </p>
                    )}
                </div>
            )}
            {provenance.baselineSet && (
                <div className="mt-3 grid gap-2 text-zinc-400 md:grid-cols-3">
                    <div>
                        Baseline status: <span className="text-white">{provenance.baselineSet.status}</span>
                    </div>
                    <div>
                        Option results: <span className="text-white">{provenance.baselineSet.sourceOptionResultCount}</span>
                    </div>
                    <div>
                        Full baselines: <span className="text-white">{provenance.baselineSet.fullBaselineCount}</span>
                    </div>
                </div>
            )}

            {provenance.targetMetric === 'option' && (
                <div className="mt-3 space-y-1 text-zinc-400">
                    <div>
                        Option target: <span className="text-white">{optionStrategyLabel}</span>
                    </div>
                    <div>
                        Open trade types:{' '}
                        <span className="text-white">{provenance.optionTarget?.allowedOpenTradeTypes.join(', ') ?? '—'}</span>
                    </div>
                    <div>
                        Option selections: <span className="text-white">{optionSelectionLabel}</span>
                    </div>
                </div>
            )}

            {provenance.validation.messages.length > 0 && (
                <div className="mt-3 space-y-1">
                    {provenance.validation.messages.map((message) => (
                        <div className="text-zinc-400" key={message}>
                            {message}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ScorecardTargetProvenancePanel;
